import { GoogleGenAI, createUserContent, createPartFromUri } from "@google/genai";
import { collection, query, orderBy, onSnapshot, getDoc, doc, setDoc, updateDoc, serverTimestamp, where, getDocs } from 'firebase/firestore';
import { db } from './firebase';

// Initialize Gemini
const genAI = new GoogleGenAI({ 
  apiKey: process.env.REACT_APP_GEMINI_API_KEY || 'AIzaSyAWckThlZKENv1m3nAcDKtLY36Zirj34nI'
});

export const chatService = {
  // Start a new chat session with a book
  async startChat(bookId, bookContent, userId) {
    console.log('Starting chat for book:', bookId, 'and user:', userId);
    console.log('Book content length:', bookContent?.length);
    
    if (!userId) {
      console.error('User ID is required to start a chat');
      throw new Error('User ID is required to start a chat');
    }
    
    // Create a unique ID for this user-book chat
    const chatId = `${userId}_${bookId}`;
    
    try {
      // Check if chat already exists
      const chatDocRef = doc(db, 'chats', chatId);
      const chatDoc = await getDoc(chatDocRef);
      
      if (chatDoc.exists()) {
        console.log('Chat already exists, loading existing chat');
        const existingChat = chatDoc.data();
        return {
          id: chatId,
          ...existingChat
        };
      }
      // --- No Gemini context caching ---
      // Get book title for system instruction
      let bookTitle = 'the book';
      try {
        const bookDoc = await getDoc(doc(db, 'books', bookId));
        if (bookDoc.exists()) {
          const bookData = bookDoc.data();
          bookTitle = bookData.title || 'the book';
        }
      } catch (e) { /* ignore */ }
      // Initialize the chat with the book's content as the first user message
      const newChat = {
        userId,
        bookId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        messages: [
          {
            role: "user",
            content: `Book context: ${bookContent}`,
            timestamp: new Date().toISOString(),
            isInitialContext: true
          },
          {
            role: "assistant",
            content: "I understand. I will help you with any questions about this book. Please ask your question.",
            timestamp: new Date().toISOString()
          }
        ]
      };
      // Save to Firestore
      await setDoc(chatDocRef, newChat);
      console.log('New chat created with ID:', chatId);
      return {
        id: chatId,
        ...newChat
      };
    } catch (error) {
      console.error('Error creating chat:', error);
      throw error;
    }
  },

  // Send a message and get response
  async sendMessage(chatId, message, userId) {
    console.log('Sending message:', { chatId, message, userId });
    if (!chatId || !message || !userId) {
      console.error('Missing required parameters:', { chatId, message, userId });
      throw new Error('Chat ID, message, and user ID are required');
    }
    try {
      // Get existing chat
      const chatDocRef = doc(db, 'chats', chatId);
      const chatDoc = await getDoc(chatDocRef);
      if (!chatDoc.exists()) {
        console.error('Chat not found:', chatId);
        throw new Error('Chat not found');
      }
      const chat = chatDoc.data();
      console.log('Existing chat:', chat);
      // Check if user has access to this chat
      if (chat.userId !== userId) {
        console.error('User does not have access to this chat');
        throw new Error('User does not have access to this chat');
      }
      // Extract book ID from chat ID
      const bookId = chat.bookId;
      // Get book title for system instruction
      let bookTitle = 'the book';
      try {
        const bookDoc = await getDoc(doc(db, 'books', bookId));
        if (bookDoc.exists()) {
          const bookData = bookDoc.data();
          bookTitle = bookData.title || 'the book';
        }
      } catch (e) { /* ignore */ }
      // Add user message to chat
      const userMessage = {
        role: "user",
        content: message,
        timestamp: new Date().toISOString()
      };
      // --- Build conversation history for Gemini chat ---
      // Only include user and assistant messages for context
      let history = (chat.messages || [])
        .filter(msg => msg.role === 'user' || msg.role === 'assistant')
        .map(msg => ({
          role: msg.role === 'assistant' ? 'model' : 'user',
          parts: [{ text: msg.content }]
        }));
      // Ensure history starts with a user turn (Gemini SDK requirement)
      while (history.length > 0 && history[0].role !== 'user') {
        history.shift();
      }
      // --- Gemini multi-turn chat interface ---
      const chatSession = genAI.chats.create({
        model: "gemini-2.0-flash-lite",
        history,
        config: {
          systemInstruction: `You are a helpful book assistant for the book "${bookTitle}". You will help users understand and discuss this specific book. Be concise and accurate in your responses, and only answer questions about this book. If asked about other books, politely explain that you are focused on this specific book.`
        }
      });
      const response = await chatSession.sendMessage({ message });
      const text = response.text;
      // --- End Gemini call ---
      // Add assistant message to chat
      const assistantMessage = {
        role: "assistant",
        content: text,
        timestamp: new Date().toISOString()
      };
      // Update the chat in Firestore
      await updateDoc(chatDocRef, {
        messages: [...chat.messages, userMessage, assistantMessage],
        updatedAt: serverTimestamp()
      });
      console.log('Chat updated with new messages');
      return {
        userMessage,
        assistantMessage
      };
    } catch (error) {
      console.error('Error in chat:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      throw error;
    }
  },

  // Get chat history for a specific user and book
  async getChatHistory(userId, bookId) {
    console.log('Getting chat history for user:', userId, 'and book:', bookId);
    
    if (!userId || !bookId) {
      console.error('User ID and Book ID are required');
      throw new Error('User ID and Book ID are required');
    }
    
    const chatId = `${userId}_${bookId}`;
    const chatDocRef = doc(db, 'chats', chatId);
    
    try {
      const chatDoc = await getDoc(chatDocRef);
      
      if (!chatDoc.exists()) {
        console.log('No chat history found, returning empty array');
        return [];
      }
      
      const chat = chatDoc.data();
      console.log(`Found chat with ${chat.messages?.length || 0} messages`);
      
      return chat.messages || [];
    } catch (error) {
      console.error('Error getting chat history:', error);
      throw error;
    }
  },
  
  // Set up real-time listener for chat updates
  setupChatListener(userId, bookId, callback) {
    console.log('Setting up chat listener for user:', userId, 'and book:', bookId);
    
    if (!userId || !bookId) {
      console.error('User ID and Book ID are required');
      throw new Error('User ID and Book ID are required');
    }
    
    const chatId = `${userId}_${bookId}`;
    const chatDocRef = doc(db, 'chats', chatId);
    
    // Set up real-time listener
    return onSnapshot(chatDocRef, (doc) => {
      if (doc.exists()) {
        const chat = doc.data();
        console.log(`Chat updated, now has ${chat.messages?.length || 0} messages`);
        callback(chat.messages || []);
      } else {
        console.log('No chat found');
        callback([]);
      }
    });
  },
  
  // Get all chats for a user
  async getUserChats(userId) {
    console.log('Getting all chats for user:', userId);
    
    if (!userId) {
      console.error('User ID is required');
      throw new Error('User ID is required');
    }
    
    try {
      const chatsRef = collection(db, 'chats');
      const q = query(chatsRef, where('userId', '==', userId), orderBy('updatedAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const userChats = [];
      querySnapshot.forEach(doc => {
        userChats.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log(`Found ${userChats.length} chats for user ${userId}`);
      
      // Fetch book details for each chat
      const chatsWithBookDetails = await Promise.all(userChats.map(async (chat) => {
        try {
          const bookDoc = await getDoc(doc(db, 'books', chat.bookId));
          if (bookDoc.exists()) {
            const bookData = bookDoc.data();
            return {
              ...chat,
              bookTitle: bookData.title,
              bookAuthor: bookData.author,
              bookCover: bookData.coverUrl
            };
          }
          return chat;
        } catch (err) {
          console.error(`Error fetching book details for chat ${chat.id}:`, err);
          return chat;
        }
      }));
      
      return chatsWithBookDetails;
    } catch (error) {
      console.error('Error getting user chats:', error);
      throw error;
    }
  }
};

// Character-based chat service
export const chatServiceCharacter = {
  // Start a new chat session with a character
  async startChat(bookId, bookContent, userId, characterName, bookTitle) {
    if (!userId || !characterName) {
      throw new Error('User ID and character name are required to start a character chat');
    }
    // Use a unique chat ID for user/book/character
    const safeChar = characterName.replace(/[^a-zA-Z0-9]/g, '_');
    const chatId = `${userId}_${bookId}_char_${safeChar}`;
    try {
      const chatDocRef = doc(db, 'chats', chatId);
      const chatDoc = await getDoc(chatDocRef);
      if (chatDoc.exists()) {
        const existingChat = chatDoc.data();
        return {
          id: chatId,
          ...existingChat,
          characterName,
        };
      }
      // System prompt for character
      const systemPrompt = `You are ${characterName} from the book "${bookTitle}". Answer as this character, in first person, and stay in character.`;
      const newChat = {
        userId,
        bookId,
        characterName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        messages: [
          {
            role: "user",
            content: `Book context: ${bookContent}`,
            timestamp: new Date().toISOString(),
            isInitialContext: true
          },
          {
            role: "assistant",
            content: `I am ${characterName}. Ask me anything!`,
            timestamp: new Date().toISOString(),
            isInitialContext: true
          }
        ],
        systemPrompt
      };
      await setDoc(chatDocRef, newChat);
      return {
        id: chatId,
        ...newChat,
        characterName,
      };
    } catch (error) {
      throw error;
    }
  },

  // Send a message and get response as character
  async sendMessage(chatId, message, userId, characterName, bookTitle) {
    if (!chatId || !message || !userId || !characterName) {
      throw new Error('Chat ID, message, user ID, and character name are required');
    }
    try {
      const chatDocRef = doc(db, 'chats', chatId);
      const chatDoc = await getDoc(chatDocRef);
      if (!chatDoc.exists()) {
        throw new Error('Chat not found');
      }
      const chat = chatDoc.data();
      if (chat.userId !== userId) {
        throw new Error('User does not have access to this chat');
      }
      // Add user message
      const userMessage = {
        role: "user",
        content: message,
        timestamp: new Date().toISOString()
      };
      // Build conversation history
      let history = (chat.messages || [])
        .filter(msg => msg.role === 'user' || msg.role === 'assistant')
        .map(msg => ({
          role: msg.role === 'assistant' ? 'model' : 'user',
          parts: [{ text: msg.content }]
        }));
      while (history.length > 0 && history[0].role !== 'user') {
        history.shift();
      }
      // System prompt for character
      const systemPrompt = chat.systemPrompt || `You are ${characterName} from the book "${bookTitle}". Answer as this character, in first person, and stay in character.`;
      const chatSession = genAI.chats.create({
        model: "gemini-2.0-flash-lite",
        history,
        config: {
          systemInstruction: systemPrompt
        }
      });
      const response = await chatSession.sendMessage({ message });
      const text = response.text;
      const assistantMessage = {
        role: "assistant",
        content: text,
        timestamp: new Date().toISOString()
      };
      await updateDoc(chatDocRef, {
        messages: [...chat.messages, userMessage, assistantMessage],
        updatedAt: serverTimestamp()
      });
      return {
        userMessage,
        assistantMessage
      };
    } catch (error) {
      throw error;
    }
  },

  // Get chat history for a character chat
  async getChatHistory(userId, bookId, characterName) {
    if (!userId || !bookId || !characterName) {
      throw new Error('User ID, Book ID, and Character Name are required');
    }
    const safeChar = characterName.replace(/[^a-zA-Z0-9]/g, '_');
    const chatId = `${userId}_${bookId}_char_${safeChar}`;
    const chatDocRef = doc(db, 'chats', chatId);
    try {
      const chatDoc = await getDoc(chatDocRef);
      if (!chatDoc.exists()) {
        return [];
      }
      const chat = chatDoc.data();
      return chat.messages || [];
    } catch (error) {
      throw error;
    }
  },

  // Set up real-time listener for character chat
  setupChatListener(userId, bookId, characterName, callback) {
    if (!userId || !bookId || !characterName) {
      throw new Error('User ID, Book ID, and Character Name are required');
    }
    const safeChar = characterName.replace(/[^a-zA-Z0-9]/g, '_');
    const chatId = `${userId}_${bookId}_char_${safeChar}`;
    const chatDocRef = doc(db, 'chats', chatId);
    return onSnapshot(chatDocRef, (doc) => {
      if (doc.exists()) {
        const chat = doc.data();
        callback(chat.messages || []);
      } else {
        callback([]);
      }
    });
  }
}; 